import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import TopBar from "../components/TopBar.jsx";
import Footer from "../components/Footer.jsx";
import Home from "../pages/Home.jsx";
import Stock from "../pages/Stock.jsx";
import CarDetail from "../pages/CarDetail.jsx";
import SignIn from "../pages/SignIn.jsx";
import { useApp } from "./AppContext.jsx";

/**
 * A lazy route that survives a deploy.
 *
 * Chunk filenames are fingerprinted, so a tab holding the shell from the previous deploy
 * asks for a chunk that no longer exists. Nothing is broken — the visitor is simply one
 * version behind — but React renders it as a hard crash on a blank page.
 *
 * So: fetch the fresh shell once, which names the current fingerprints. `sessionStorage`
 * keeps it to one attempt, because the same failure with a fresh document is a real error
 * and reloading again would only loop.
 */
const RELOADED = "apex_chunk_reload";
function lazyRoute(load) {
  return lazy(() =>
    load().then(
      (m) => { try { sessionStorage.removeItem(RELOADED); } catch {} return m; },
      (err) => {
        let already = true;
        try { already = sessionStorage.getItem(RELOADED) === "1"; } catch {}
        if (already) throw err;
        try { sessionStorage.setItem(RELOADED, "1"); } catch {}
        window.location.reload();
        return new Promise(() => {});          // hold the boundary while the page goes away
      },
    ));
}

// Split the routes a browsing visitor never reaches out of the initial bundle.
const Auctions = lazyRoute(() => import("../pages/Auctions.jsx"));
const Calculator = lazyRoute(() => import("../pages/Calculator.jsx"));
const HowItWorks = lazyRoute(() => import("../pages/HowItWorks.jsx"));
const Wanted = lazyRoute(() => import("../pages/Wanted.jsx"));
const Admin = lazyRoute(() => import("../pages/Admin.jsx"));
const NotFound = lazyRoute(() => import("../pages/NotFound.jsx"));

/**
 * The wall, on the browser's side of it.
 *
 * The real wall is the API, which answers 401 to anyone without a session — this only
 * decides what to draw instead, and it draws the sign-in form in place of the page rather
 * than redirecting to /signin. That keeps the URL the visitor asked for in the address bar,
 * so the link mailed to them returns to the car they clicked and not to the front door.
 *
 * Nothing is rendered until we know: showing a sign-in form to a signed-in customer for the
 * half-second before /api/auth/me answers reads as being logged out.
 */
function Guarded({ children }) {
  const { user, authReady, wall } = useApp();
  if (!authReady) return <div className="wrap page"><div className="skeleton" style={{ height: 300 }} /></div>;
  if (!wall) return children;          // the server is not gating; do not pretend otherwise
  return user ? children : <SignIn />;
}

/** Restore scroll on navigation — React Router keeps the old offset otherwise. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <TopBar />
      <main>
        <Suspense fallback={<div className="wrap page"><div className="skeleton" style={{ height: 300 }} /></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/signin" element={<SignIn redirect="/stock" />} />
            <Route path="/stock" element={<Guarded><Stock /></Guarded>} />
            <Route path="/car/:id" element={<Guarded><CarDetail /></Guarded>} />
            <Route path="/auctions" element={<Guarded><Auctions /></Guarded>} />
            <Route path="/import-calculator" element={<Guarded><Calculator /></Guarded>} />
            <Route path="/wanted" element={<Guarded><Wanted /></Guarded>} />
            {/* Public on purpose: how it works and the landing page are what persuade
                someone to hand over an address in the first place. */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}
